import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined"
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded"
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined"
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded"
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined"


import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"


import MainLayout from "../../layouts/MainLayout"

import {
  getDepotRelaisDashboard,
} from "../../services/depotRelaisDashboardService"

import {
  getBusinessLabel,
  getDashboardConfig,
} from "../../config/appConfig"


const DASHBOARD_SURFACE = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.045)",
  backgroundColor: "background.paper",
}

const DASHBOARD_FILTER_SURFACE = {
  ...DASHBOARD_SURFACE,
  p: { xs: 1.5, sm: 2 },
  mb: 2.5,
}

const DASHBOARD_SECTION_GAP = {
  xs: 1.25,
  sm: 1.5,
  md: 2,
}

const MAIN_COMPANY = getBusinessLabel("mainCompany", "Entreprise principale")
const MAIN_COMPANY_SHORT = getBusinessLabel("mainCompanyShort", MAIN_COMPANY)

const RELAY_DEPOT = getBusinessLabel("relayDepot", "Structure secondaire")
const RELAY_DEPOTS = getBusinessLabel("relayDepots", "Structures secondaires")

const SHOP = getBusinessLabel("shop", "Shop")
const SHOPS = getBusinessLabel("shops", "Shops")

const COMPANY_PRICE = getBusinessLabel("companyPrice", "Prix entreprise")
const SUPPLIER_DEBT = getBusinessLabel(
  "supplierDebt",
  `Dette envers ${MAIN_COMPANY.toLowerCase()}`
)

const VALIDATION_COMPANY = getBusinessLabel(
  "validationCompany",
  `Validation ${MAIN_COMPANY.toLowerCase()}`
)

const VALIDATION_RELAY = getBusinessLabel(
  "validationRelay",
  `Validation ${RELAY_DEPOT.toLowerCase()}`
)

const RELAY_DASHBOARD_CONFIG = getDashboardConfig("relay")

const RELAY_DASHBOARD_TITLE =
  RELAY_DASHBOARD_CONFIG?.label || `Dashboard ${RELAY_DEPOT}`

const RELAY_DASHBOARD_DESCRIPTION =
  RELAY_DASHBOARD_CONFIG?.description ||
  `Suivi des ${RELAY_DEPOTS.toLowerCase()}, ventes, dettes, stock, paiements et rentabilité.`

const EMPTY_DASHBOARD = {
  scope: {
    organization_id: null,
    organization_code: "",
    organization_name: "",
    store_id: null,
    is_store_locked: false,
    role: "",
  },

  filters: {
    date_from: null,
    date_to: null,
    stores: [],
  },

  kpis: {},
  sales_by_day: [],
  sales_by_store: [],
  top_products: [],
  low_stock: [],
  dormant_products: [],
  recent_orders: [],
}

const ORDER_STATUS_LABELS = {
  pending_depot_relais: `En attente ${RELAY_DEPOT}`,

  pending_kin_mousse: `En attente ${MAIN_COMPANY}`,

  approved_by_kin_mousse: `Approuvée par ${MAIN_COMPANY}`,

  shipped: "Expédiée",

  received_by_shop: `Reçue par le ${SHOP.toLowerCase()}`,

  rejected: "Rejetée",

  cancelled: "Annulée",
}

const ORDER_STATUS_COLORS = {
  pending_depot_relais: "warning",
  pending_kin_mousse: "warning",
  approved_by_kin_mousse: "info",
  shipped: "primary",
  received_by_shop: "success",
  rejected: "error",
  cancelled: "default",
}

const toNumber = (value) => {
  const number = Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}

const formatMoney = (value) => {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}

const formatNumber = (value) => {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      maximumFractionDigits: 2,
    }
  )
}

const formatDate = (value) => {
  if (!value) {
    return "-"
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return date.toLocaleDateString(
    "fr-FR"
  )
}

function KpiCard({
  title,
  value,
  subtitle,
  icon,
  color = "#2563eb",
  valueColor = "text.primary",
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...DASHBOARD_SURFACE,
        height: "100%",
        minHeight: 118,
        p: 2,
        display: "flex",
        alignItems: "center",
        transition: "transform .18s ease, box-shadow .18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 16px 34px rgba(15, 23, 42, 0.075)",
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ width: "100%", minWidth: 0 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: color,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 8px 18px ${color}2e`,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            fontWeight={700}
            sx={{
              lineHeight: 1.25,
              mb: 0.35,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="h5"
            fontWeight={900}
            color={valueColor}
            sx={{
              lineHeight: 1.1,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>

          {subtitle ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.65,
                lineHeight: 1.35,
              }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  )
}

function SectionTitle({
  title,
  subtitle,
}) {
  return (
    <Box mb={1.35}>
      <Typography
        variant="h6"
        fontWeight={900}
      >
        {title}
      </Typography>

      {subtitle ? (
        <Typography
          variant="body2"
          color="text.secondary"
          mt={0.25}
        >
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )
}

function DataTable({
  rows,
  columns,
  loading,
  pageSize = 5,
  height = 390,
}) {
  return (
    <Box
      sx={{
        width: "100%",
        height,
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[
          5,
          10,
          20,
          25,
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize,
              page: 0,
            },
          },
        }}
        sx={{
          border: 0,

          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f8fafc",
            fontWeight: 800,
          },

          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f8fafc",
          },
        }}
      />
    </Box>
  )
}

function DepotRelaisDashboardPage() {

  const [dashboard, setDashboard] =
    useState(EMPTY_DASHBOARD)

  const [filters, setFilters] =
    useState({
      date_from: "",
      date_to: "",
      store_id: "",
    })

  const [loading, setLoading] =
    useState(false)

  const [loadError, setLoadError] =
    useState("")

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      severity: "success",
      message: "",
    })

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }

  const closeSnackbar = () => {
    setSnackbar((previous) => ({
      ...previous,
      open: false,
    }))
  }

  const loadDashboard = async (
    nextFilters = filters
  ) => {
    try {
      setLoading(true)
      setLoadError("")

      const data =
        await getDepotRelaisDashboard({
          dateFrom:
            nextFilters.date_from,

          dateTo:
            nextFilters.date_to,

          storeId:
            nextFilters.store_id,
        })

      const safeData = {
        ...EMPTY_DASHBOARD,
        ...data,

        scope: {
          ...EMPTY_DASHBOARD.scope,
          ...(data?.scope || {}),
        },

        filters: {
          ...EMPTY_DASHBOARD.filters,
          ...(data?.filters || {}),

          stores: Array.isArray(
            data?.filters?.stores
          )
            ? data.filters.stores
            : [],
        },

        kpis:
          data?.kpis || {},

        sales_by_day:
          Array.isArray(
            data?.sales_by_day
          )
            ? data.sales_by_day
            : [],

        sales_by_store:
          Array.isArray(
            data?.sales_by_store
          )
            ? data.sales_by_store
            : [],

        top_products:
          Array.isArray(
            data?.top_products
          )
            ? data.top_products
            : [],

        low_stock:
          Array.isArray(
            data?.low_stock
          )
            ? data.low_stock
            : [],

        dormant_products:
          Array.isArray(
            data?.dormant_products
          )
            ? data.dormant_products
            : [],

        recent_orders:
          Array.isArray(
            data?.recent_orders
          )
            ? data.recent_orders
            : [],
      }

      setDashboard(safeData)

      const selectedStoreStillExists =
        safeData.filters.stores.some(
          (store) =>
            String(store.id) ===
            String(
              nextFilters.store_id
            )
        )

      if (
        nextFilters.store_id &&
        !selectedStoreStillExists
      ) {
        setFilters((previous) => ({
          ...previous,
          store_id: "",
        }))
      }
    } catch (error) {
      console.error(
        `Erreur dashboard ${RELAY_DEPOT} :`,
        error
      )

      const message =
        error?.response?.data?.detail ||
        `Impossible de charger le dashboard ${RELAY_DEPOT}.`

      setLoadError(message)

      showSnackbar(
        message,
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDashboard({
      date_from: "",
      date_to: "",
      store_id: "",
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFilterChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target

    setFilters((previous) => ({
      ...previous,
      [name]: value,
    }))
  }

  const handleApplyFilters = () => {
    loadDashboard(filters)
  }

  const handleResetFilters = () => {
    const resetFilters = {
      date_from: "",
      date_to: "",
      store_id: "",
    }

    setFilters(resetFilters)
    loadDashboard(resetFilters)
  }

  const validStoreValue =
    useMemo(() => {
      if (!filters.store_id) {
        return ""
      }

      const exists =
        dashboard.filters.stores.some(
          (store) =>
            String(store.id) ===
            String(filters.store_id)
        )

      return exists
        ? filters.store_id
        : ""
    }, [
      dashboard.filters.stores,
      filters.store_id,
    ])

  const kpis =
    dashboard.kpis || {}

  const salesByDayRows =
    useMemo(() => {
      return dashboard.sales_by_day.map(
        (item, index) => ({
          ...item,

          id:
            item.period ||
            `sales-day-${index}`,
        })
      )
    }, [
      dashboard.sales_by_day,
    ])

  const salesByStoreRows =
    useMemo(() => {
      return dashboard.sales_by_store.map(
        (item, index) => ({
          ...item,

          id:
            item.store_id ||
            `sales-store-${index}`,
        })
      )
    }, [
      dashboard.sales_by_store,
    ])

  const topProductRows =
    useMemo(() => {
      return dashboard.top_products.map(
        (item, index) => ({
          ...item,

          id:
            item.product_id ||
            `top-product-${index}`,
        })
      )
    }, [
      dashboard.top_products,
    ])

  const lowStockRows =
    useMemo(() => {
      return dashboard.low_stock.map(
        (item, index) => ({
          ...item,

          id:
            `${item.store_id || "store"}-${
              item.product_id || index
            }`,
        })
      )
    }, [
      dashboard.low_stock,
    ])

  const dormantProductRows =
    useMemo(() => {
      return dashboard.dormant_products.map(
        (item, index) => ({
          ...item,

          id:
            `${item.store_id || "store"}-${
              item.product_id || index
            }`,
        })
      )
    }, [
      dashboard.dormant_products,
    ])

  const recentOrderRows =
    useMemo(() => {
      return dashboard.recent_orders.map(
        (item, index) => ({
          ...item,

          id:
            item.id ||
            `recent-order-${index}`,
        })
      )
    }, [
      dashboard.recent_orders,
    ])


  const salesByStoreChartData =
    useMemo(() => {
      return dashboard.sales_by_store
        .map((item) => ({
          name:
            item.store_name ||
            item.store_code ||
            SHOP,

          full_name:
            item.store_name &&
            item.store_code
              ? `${item.store_name} (${item.store_code})`
              : item.store_name ||
                item.store_code ||
                SHOP,

          sales:
            toNumber(
              item.total_sales
            ),

          paid:
            toNumber(
              item.total_paid
            ),

          remaining:
            toNumber(
              item.total_remaining
            ),
        }))
        .filter(
          (item) =>
            item.sales > 0 ||
            item.paid > 0 ||
            item.remaining > 0
        )
    }, [
      dashboard.sales_by_store,
    ])


  const salesByDayChartData =
    useMemo(() => {
      return dashboard.sales_by_day
        .map((item) => ({
          date:
            formatDate(
              item.period
            ),

          sales:
            toNumber(
              item.total_sales
            ),

          paid:
            toNumber(
              item.total_paid
            ),

          remaining:
            toNumber(
              item.total_remaining
            ),
        }))
        .filter(
          (item) =>
            item.sales > 0 ||
            item.paid > 0 ||
            item.remaining > 0
        )
    }, [
      dashboard.sales_by_day,
    ])


  const topProductsChartData =
    useMemo(() => {
      return [...dashboard.top_products]
        .sort(
          (a, b) =>
            toNumber(
              b.quantity_sold
            ) -
            toNumber(
              a.quantity_sold
            )
        )
        .slice(
          0,
          5
        )
        .map((item) => ({
          name:
            item.product_name ||
            item.product_code ||
            "Produit",

          full_name:
            item.product_name &&
            item.product_code
              ? `${item.product_name} (${item.product_code})`
              : item.product_name ||
                item.product_code ||
                "Produit",

          quantity:
            toNumber(
              item.quantity_sold
            ),

          sales:
            toNumber(
              item.total_sales
            ),

          margin:
            toNumber(
              item.gross_margin
            ),
        }))
    }, [
      dashboard.top_products,
    ])


  const stockValueChartData =
    useMemo(() => {
      return [
        {
          name: "Valeur achat",
          value:
            Math.max(
              0,
              toNumber(
                kpis.stock_purchase_value
              )
            ),
        },
        {
          name: "Marge potentielle",
          value:
            Math.max(
              0,
              toNumber(
                kpis.stock_potential_margin
              )
            ),
        },
      ].filter(
        (item) =>
          item.value > 0
      )
    }, [
      kpis.stock_purchase_value,
      kpis.stock_potential_margin,
    ])


  const chartColors = [
    "#1976d2",
    "#2e7d32",
    "#ed6c02",
  ]

  const salesByDayColumns =
    useMemo(
      () => [
        {
          field: "period",
          headerName: "Date",
          minWidth: 120,
          flex: 1,

          renderCell: (params) =>
            formatDate(
              params.value
            ),
        },
        {
          field: "sales_count",
          headerName: "Ventes",
          width: 100,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field: "total_sales",
          headerName:
            "Chiffre d’affaires",

          minWidth: 165,
          flex: 1.2,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "total_paid",
          headerName: "Encaissé",
          minWidth: 140,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "total_remaining",
          headerName: "Reste clients",
          minWidth: 145,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
      ],
      []
    )

  const salesByStoreColumns =
    useMemo(
      () => [
        {
          field: "store_code",
          headerName: "Code",
          minWidth: 100,
          flex: 0.7,
        },
        {
          field: "store_name",
          headerName: SHOP,
          minWidth: 160,
          flex: 1.2,
        },
        {
          field: "sales_count",
          headerName: "Ventes",
          width: 100,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field: "total_sales",
          headerName:
            "Chiffre d’affaires",

          minWidth: 170,
          flex: 1.2,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "total_paid",
          headerName: "Encaissé",
          minWidth: 145,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "total_remaining",
          headerName:
            "Créances clients",

          minWidth: 160,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
      ],
      []
    )

  const topProductColumns =
    useMemo(
      () => [
        {
          field: "product_code",
          headerName: "Code",
          minWidth: 110,
          flex: 0.7,
        },
        {
          field: "product_name",
          headerName: "Produit",
          minWidth: 190,
          flex: 1.4,
        },
        {
          field: "quantity_sold",
          headerName:
            "Quantité vendue",

          minWidth: 140,
          flex: 0.8,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field: "total_sales",
          headerName: "Ventes",
          minWidth: 135,
          flex: 0.9,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "estimated_cost",
          headerName:
            `Coût ${MAIN_COMPANY_SHORT}`,

          minWidth: 155,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "gross_margin",
          headerName: "Marge brute",
          minWidth: 140,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
      ],
      []
    )

  const lowStockColumns =
    useMemo(
      () => [
        {
          field: "store_code",
          headerName: SHOP,
          minWidth: 100,
          flex: 0.7,
        },
        {
          field: "product_code",
          headerName: "Code produit",
          minWidth: 120,
          flex: 0.8,
        },
        {
          field: "product_name",
          headerName: "Produit",
          minWidth: 190,
          flex: 1.4,
        },
        {
          field: "quantity",
          headerName:
            "Quantité disponible",

          minWidth: 150,
          flex: 0.9,

          renderCell: (params) => (
            <Chip
              label={formatNumber(
                params.value
              )}
              size="small"
              color={
                toNumber(
                  params.value
                ) <= 0
                  ? "error"
                  : toNumber(
                      params.value
                    ) <= 5
                  ? "warning"
                  : "default"
              }
            />
          ),
        },
      ],
      []
    )

  const dormantProductColumns =
    useMemo(
      () => [
        {
          field: "store_code",
          headerName: SHOP,
          minWidth: 100,
          flex: 0.7,
        },
        {
          field: "product_code",
          headerName: "Code produit",
          minWidth: 120,
          flex: 0.8,
        },
        {
          field: "product_name",
          headerName: "Produit",
          minWidth: 190,
          flex: 1.4,
        },
        {
          field: "quantity",
          headerName: "Quantité",
          minWidth: 105,
          flex: 0.7,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field:
            "stock_purchase_value",

          headerName: "Valeur achat",
          minWidth: 145,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field:
            "stock_sale_value",

          headerName: "Valeur vente",
          minWidth: 145,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
      ],
      []
    )

  const recentOrderColumns =
    useMemo(
      () => [
        {
          field: "order_code",
          headerName: "Commande",
          minWidth: 145,
          flex: 1,
        },
        {
          field: "store_name",
          headerName: SHOP,
          minWidth: 150,
          flex: 1,
        },
        {
          field: "status",
          headerName: "Statut",
          minWidth: 185,
          flex: 1.1,

          renderCell: (params) => (
            <Chip
              label={
                ORDER_STATUS_LABELS[
                  params.value
                ] ||
                params.value ||
                "-"
              }
              size="small"
              color={
                ORDER_STATUS_COLORS[
                  params.value
                ] ||
                "default"
              }
            />
          ),
        },
        {
          field:
            "requested_quantity",

          headerName: "Demandée",
          minWidth: 105,
          flex: 0.7,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field:
            "received_quantity",

          headerName: "Reçue",
          minWidth: 95,
          flex: 0.7,

          renderCell: (params) =>
            formatNumber(
              params.value
            ),
        },
        {
          field:
            "supplier_total_amount",

          headerName:
            SUPPLIER_DEBT,

          minWidth: 190,
          flex: 1.1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field:
            "expected_margin",

          headerName:
            "Marge attendue",

          minWidth: 145,
          flex: 1,

          renderCell: (params) =>
            formatMoney(
              params.value
            ),
        },
        {
          field: "created_at",
          headerName: "Date",
          minWidth: 115,
          flex: 0.8,

          renderCell: (params) =>
            formatDate(
              params.value
            ),
        },
      ],
      []
    )

  return (
    <MainLayout>
      <Box sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
      <Box sx={{ mb: 2.25 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "flex-end" }}
          spacing={1.5}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
                lineHeight: 1.08,
              }}
            >
              {RELAY_DASHBOARD_TITLE}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.45,
                maxWidth: 900,
              }}
            >
              {RELAY_DASHBOARD_DESCRIPTION}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => loadDashboard(filters)}
            disabled={loading}
            sx={{
              minHeight: 44,
              px: 2.2,
              borderRadius: 2,
              fontWeight: 800,
              width: {
                xs: "100%",
                md: "auto",
              },
              flexShrink: 0,
            }}
          >
            {loading ? "Actualisation..." : "Actualiser"}
          </Button>
        </Stack>
      </Box>

      {loadError ? (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 3,
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                loadDashboard(filters)
              }
              disabled={loading}
            >
              Réessayer
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          ...DASHBOARD_FILTER_SURFACE,
          p: { xs: 1.4, sm: 1.6 },
        }}
      >
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          <TextField
            label="Date début"
            name="date_from"
            type="date"
            size="small"
            value={filters.date_from}
            onChange={handleFilterChange}
            fullWidth
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          <TextField
            label="Date fin"
            name="date_to"
            type="date"
            size="small"
            value={filters.date_to}
            onChange={handleFilterChange}
            fullWidth
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
          />

          <TextField
            select
            label={SHOP}
            name="store_id"
            size="small"
            value={validStoreValue}
            onChange={handleFilterChange}
            fullWidth
            sx={{
              minWidth: {
                xs: "100%",
                lg: 260,
              },
            }}
          >
            <MenuItem value="">
              Tous les {SHOPS.toLowerCase()}
            </MenuItem>

            {dashboard.filters.stores.map((store) => (
              <MenuItem
                key={store.id}
                value={String(store.id)}
              >
                {store.code} - {store.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            disabled={loading}
            sx={{
              minHeight: 40,
              px: 2.4,
              minWidth: {
                xs: "100%",
                lg: 132,
              },
              fontWeight: 800,
              borderRadius: 2,
            }}
          >
            Appliquer
          </Button>

          <Button
            variant="outlined"
            onClick={handleResetFilters}
            disabled={loading}
            sx={{
              minHeight: 40,
              px: 2.1,
              minWidth: {
                xs: "100%",
                lg: 132,
              },
              fontWeight: 800,
              borderRadius: 2,
            }}
          >
            Réinitialiser
          </Button>
        </Stack>

        {dashboard.filters.stores.length === 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Aucun {SHOP.toLowerCase()} lié à cette structure secondaire.
          </Typography>
        ) : null}
      </Paper>

      <SectionTitle
        title="Ventes et encaissements"
        subtitle={`Performance commerciale de ${RELAY_DEPOT} sur la période sélectionnée.`}
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title="Chiffre d’affaires"
          value={formatMoney(
            kpis.total_sales
          )}
          subtitle={`${formatNumber(
            kpis.sales_count
          )} vente(s) enregistrée(s)`}
        
          icon={<TrendingUpRoundedIcon />}
          color="#2563eb"
        />

        <KpiCard
          title="Montant encaissé"
          value={formatMoney(
            kpis.total_paid
          )}
          subtitle="Paiements reçus des clients"
          valueColor="success.main"
        
          icon={<PaymentsOutlinedIcon />}
          color="#16a34a"
        />

        <KpiCard
          title="Reste clients à payer"
          value={formatMoney(
            kpis.total_remaining
          )}
          subtitle="Part non encore encaissée sur les ventes"
          valueColor="warning.main"
        
          icon={<WarningAmberRoundedIcon />}
          color="#f97316"
        />

        <KpiCard
          title="Créances clients ouvertes"
          value={formatMoney(
            kpis.customer_debt_remaining
          )}
          subtitle={`${formatNumber(
            kpis.customer_debt_count
          )} créance(s) client(s)`}
        
          icon={<PaymentsOutlinedIcon />}
          color="#c2410c"
        />
      </Box>

      <SectionTitle
        title={SUPPLIER_DEBT}
        subtitle={`Situation du stock reçu à crédit auprès de ${MAIN_COMPANY}.`}
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title={`Dette initiale ${MAIN_COMPANY_SHORT}`}
          value={formatMoney(
            kpis.debt_original
          )}
          subtitle={`${formatNumber(
            kpis.debt_count
          )} dette(s) fournisseur`}
        
          icon={<WarningAmberRoundedIcon />}
          color="#dc2626"
        />

        <KpiCard
          title="Dette déjà payée"
          value={formatMoney(
            kpis.debt_paid
          )}
          subtitle="Total des règlements confirmés"
          valueColor="success.main"
        
          icon={<PaymentsOutlinedIcon />}
          color="#16a34a"
        />

        <KpiCard
          title="Dette restante"
          value={formatMoney(
            kpis.debt_remaining
          )}
          subtitle={`Montant encore dû à ${MAIN_COMPANY}`}
          valueColor="error.main"
        
          icon={<WarningAmberRoundedIcon />}
          color="#991b1b"
        />

        <KpiCard
          title="Paiements sur la période"
          value={formatMoney(
            kpis.supplier_payments_period
          )}
          subtitle={`${formatNumber(
            kpis.supplier_payments_count
          )} règlement(s) effectué(s)`}
        
          icon={<PaymentsOutlinedIcon />}
          color="#0284c7"
        />
      </Box>

      <SectionTitle
        title="Marge, dépenses et bénéfice"
        subtitle="Le paiement de la dette réduit la trésorerie, mais il ne doit pas être soustrait une seconde fois du bénéfice."
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(4, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title="Coût des produits vendus"
          value={formatMoney(
            kpis.estimated_cost
          )}
          subtitle={`Valeur estimée au ${COMPANY_PRICE.toLowerCase()}`}
        
          icon={<Inventory2OutlinedIcon />}
          color="#7c3aed"
        />

        <KpiCard
          title="Marge brute"
          value={formatMoney(
            kpis.gross_margin
          )}
          subtitle="Ventes moins coût des produits vendus"
          valueColor="success.main"
        
          icon={<TrendingUpRoundedIcon />}
          color="#16a34a"
        />

        <KpiCard
          title="Dépenses"
          value={formatMoney(
            kpis.total_expenses
          )}
          subtitle={`${formatNumber(
            kpis.expenses_count
          )} dépense(s) confirmée(s)`}
          valueColor="warning.main"
        
          icon={<PaymentsOutlinedIcon />}
          color="#f97316"
        />

        <KpiCard
          title="Bénéfice net estimé"
          value={formatMoney(
            kpis.net_profit
          )}
          subtitle="Marge brute moins dépenses"
          valueColor={
            toNumber(
              kpis.net_profit
            ) >= 0
              ? "success.main"
              : "error.main"
          }
        
          icon={<TrendingUpRoundedIcon />}
          color="#15803d"
        />
      </Box>

      <SectionTitle
        title="Trésorerie estimée"
        subtitle={`Encaissements clients moins les dépenses et les règlements effectués à ${MAIN_COMPANY}.`}
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title={`Cash avant paiement ${MAIN_COMPANY_SHORT}`}
          value={formatMoney(
            kpis.cash_before_supplier_payment
          )}
          subtitle="Encaissements clients moins dépenses"
        
          icon={<PaymentsOutlinedIcon />}
          color="#0891b2"
        />

        <KpiCard
          title={`Cash après paiement ${MAIN_COMPANY_SHORT}`}
          value={formatMoney(
            kpis.cash_after_supplier_payment
          )}
          subtitle={`Encaissements moins dépenses et règlements ${MAIN_COMPANY_SHORT}`}
        
          icon={<PaymentsOutlinedIcon />}
          color="#1d4ed8"
        />
      </Box>

      <SectionTitle
        title="Situation du stock"
        subtitle={`Valeur du stock actuel dans les ${SHOPS.toLowerCase()} ${RELAY_DEPOT}.`}
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(5, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title="Quantité en stock"
          value={formatNumber(
            kpis.total_stock_quantity
          )}
          subtitle="Quantité physique totale"
        
          icon={<Inventory2OutlinedIcon />}
          color="#2563eb"
        />

        <KpiCard
          title="Produits en stock"
          value={formatNumber(
            kpis.products_in_stock
          )}
          subtitle="Nombre de références présentes"
        
          icon={<CategoryOutlinedIcon />}
          color="#0891b2"
        />

        <KpiCard
          title="Valeur d’achat"
          value={formatMoney(
            kpis.stock_purchase_value
          )}
          subtitle={`Stock valorisé au ${COMPANY_PRICE.toLowerCase()}`}
        
          icon={<PaymentsOutlinedIcon />}
          color="#7c3aed"
        />

        <KpiCard
          title="Valeur de vente"
          value={formatMoney(
            kpis.stock_sale_value
          )}
          subtitle="Valeur potentielle aux prix de vente"
        
          icon={<TrendingUpRoundedIcon />}
          color="#16a34a"
        />

        <KpiCard
          title="Marge potentielle"
          value={formatMoney(
            kpis.stock_potential_margin
          )}
          subtitle="Marge attendue sur le stock restant"
        
          icon={<TrendingUpRoundedIcon />}
          color="#15803d"
        />
      </Box>

      <SectionTitle
        title="Commandes de stock"
        subtitle="Suivi des demandes, validations, expéditions et réceptions."
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
            xl: "repeat(7, minmax(0, 1fr))",
          },

          gap: 1.75,
          mb: 3,
        }}
      >
        <KpiCard
          title="Total"
          value={formatNumber(
            kpis.stock_orders_total
          )}
          subtitle="Toutes les commandes"
        
          icon={<Inventory2OutlinedIcon />}
          color="#2563eb"
        />

        <KpiCard
          title={`Attente ${RELAY_DEPOT}`}
          value={formatNumber(
            kpis.stock_orders_pending_depot_relais
          )}
          subtitle={VALIDATION_RELAY}
        
          icon={<WarningAmberRoundedIcon />}
          color="#f59e0b"
        />

        <KpiCard
          title={`Attente ${MAIN_COMPANY_SHORT}`}
          value={formatNumber(
            kpis.stock_orders_pending_kin_mousse
          )}
          subtitle={VALIDATION_COMPANY}
        
          icon={<WarningAmberRoundedIcon />}
          color="#d97706"
        />

        <KpiCard
          title="Approuvées"
          value={formatNumber(
            kpis.stock_orders_approved_by_kin_mousse
          )}
          subtitle={`Approuvées par ${MAIN_COMPANY}`}
        
          icon={<SwapHorizRoundedIcon />}
          color="#0284c7"
        />

        <KpiCard
          title="Expédiées"
          value={formatNumber(
            kpis.stock_orders_shipped
          )}
          subtitle={`En route vers les ${SHOPS.toLowerCase()}`}
        
          icon={<LocalShippingOutlinedIcon />}
          color="#1d4ed8"
        />

        <KpiCard
          title="Reçues"
          value={formatNumber(
            kpis.stock_orders_received
          )}
          subtitle="Réception confirmée"
        
          icon={<WarehouseOutlinedIcon />}
          color="#16a34a"
        />

        <KpiCard
          title="Rejetées"
          value={formatNumber(
            kpis.stock_orders_rejected
          )}
          subtitle="Commandes rejetées ou annulées"
        
          icon={<WarningAmberRoundedIcon />}
          color="#dc2626"
        />
      </Box>

      {/* =================================================
          GRAPHIQUES
          ================================================= */}

      <SectionTitle
        title="Analyse visuelle"
        subtitle={`Vue synthétique des ventes, du stock et des produits de ${RELAY_DEPOT}.`}
      />

      <Box
        sx={{
          display: "grid",

          gridTemplateColumns: {
            xs: "1fr",
            xl: "repeat(2, minmax(0, 1fr))",
          },

          gap: 2,
          mb: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            ...DASHBOARD_SURFACE,
            minWidth: 0,
          }}
        >
          <SectionTitle
            title={`Ventes par ${SHOP.toLowerCase()}`}
            subtitle={`Comparaison du chiffre d'affaires, des encaissements et des créances par ${SHOP.toLowerCase()}.`}
          />

          <Box
            sx={{
              width: "100%",
              height: {
                xs: 300,
                sm: 330,
              },
            }}
          >
            {salesByStoreChartData.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  color="text.secondary"
                >
                  Aucune donnée disponible.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <BarChart
                  data={
                    salesByStoreChartData
                  }
                  margin={{
                    top: 10,
                    right: 15,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />

                  <XAxis
                    dataKey="name"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    tick={{
                      fontSize: 12,
                    }}
                  />

                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={70}
                  />

                  <RechartsTooltip
                    formatter={(
                      value,
                      name
                    ) => [
                      formatMoney(
                        value
                      ),
                      name === "sales"
                        ? "Ventes"
                        : name === "paid"
                        ? "Encaissé"
                        : "Reste clients",
                    ]}
                    labelFormatter={(
                      label,
                      payload
                    ) =>
                      payload?.[0]
                        ?.payload
                        ?.full_name ||
                      label
                    }
                  />

                  <Legend />

                  <Bar
                    dataKey="sales"
                    name="Ventes"
                    fill="#1976d2"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                    maxBarSize={54}
                  />

                  <Bar
                    dataKey="paid"
                    name="Encaissé"
                    fill="#2e7d32"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                    maxBarSize={54}
                  />

                  <Bar
                    dataKey="remaining"
                    name="Reste clients"
                    fill="#ed6c02"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                    maxBarSize={54}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>


        <Paper
          elevation={0}
          sx={{
            p: 2,
            ...DASHBOARD_SURFACE,
            minWidth: 0,
          }}
        >
          <SectionTitle
            title="Répartition de la valeur du stock"
            subtitle="Part de la valeur d'achat et de la marge potentielle du stock restant."
          />

          <Box
            sx={{
              width: "100%",
              height: {
                xs: 300,
                sm: 330,
              },
            }}
          >
            {stockValueChartData.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  color="text.secondary"
                >
                  Aucune valeur de stock disponible.
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer
                width="100%"
                height="100%"
              >
                <PieChart>
                  <Pie
                    data={
                      stockValueChartData
                    }
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="45%"
                    innerRadius={65}
                    outerRadius={100}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {stockValueChartData.map(
                      (
                        item,
                        index
                      ) => (
                        <Cell
                          key={`${item.name}-${index}`}
                          fill={
                            chartColors[
                              index %
                                chartColors.length
                            ]
                          }
                        />
                      )
                    )}
                  </Pie>

                  <RechartsTooltip
                    formatter={(
                      value
                    ) => [
                      formatMoney(
                        value
                      ),
                      "Montant",
                    ]}
                  />

                  <Legend
                    verticalAlign="bottom"
                    height={42}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>


      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          ...DASHBOARD_SURFACE,
          minWidth: 0,
        }}
      >
        <SectionTitle
          title="Top 5 produits vendus"
          subtitle="Produits les plus vendus selon les quantités enregistrées."
        />

        <Box
          sx={{
            width: "100%",
            height: {
              xs: 300,
              sm: 330,
            },
          }}
        >
          {topProductsChartData.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                color="text.secondary"
              >
                Aucun produit vendu disponible.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <BarChart
                data={
                  topProductsChartData
                }
                layout="vertical"
                margin={{
                  top: 10,
                  right: 20,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                />

                <XAxis
                  type="number"
                  tickLine={false}
                  axisLine={false}
                />

                <YAxis
                  type="category"
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  width={160}
                  tick={{
                    fontSize: 12,
                  }}
                />

                <RechartsTooltip
                  formatter={(
                    value,
                    name
                  ) => [
                    name === "quantity"
                      ? formatNumber(
                          value
                        )
                      : formatMoney(
                          value
                        ),
                    name === "quantity"
                      ? "Quantité"
                      : name === "sales"
                      ? "Ventes"
                      : "Marge",
                  ]}
                  labelFormatter={(
                    label,
                    payload
                  ) =>
                    payload?.[0]
                      ?.payload
                      ?.full_name ||
                    label
                  }
                />

                <Legend />

                <Bar
                  dataKey="quantity"
                  name="Quantité vendue"
                  fill="#2e7d32"
                  radius={[
                    0,
                    8,
                    8,
                    0,
                  ]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>


      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          ...DASHBOARD_SURFACE,
          minWidth: 0,
        }}
      >
        <SectionTitle
          title="Évolution journalière des ventes"
          subtitle="Tendance du chiffre d'affaires et des encaissements sur la période sélectionnée."
        />

        <Box
          sx={{
            width: "100%",
            height: {
              xs: 300,
              sm: 330,
            },
          }}
        >
          {salesByDayChartData.length === 0 ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                color="text.secondary"
              >
                Aucune évolution journalière disponible.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer
              width="100%"
              height="100%"
            >
              <LineChart
                data={
                  salesByDayChartData
                }
                margin={{
                  top: 10,
                  right: 20,
                  left: 0,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />

                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fontSize: 12,
                  }}
                />

                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={70}
                />

                <RechartsTooltip
                  formatter={(
                    value,
                    name
                  ) => [
                    formatMoney(
                      value
                    ),
                    name === "sales"
                      ? "Ventes"
                      : name === "paid"
                      ? "Encaissé"
                      : "Reste clients",
                  ]}
                />

                <Legend />

                <Line
                  type="monotone"
                  dataKey="sales"
                  name="Ventes"
                  stroke="#1976d2"
                  strokeWidth={3}
                  dot={{
                    r: 3,
                  }}
                  activeDot={{
                    r: 5,
                  }}
                />

                <Line
                  type="monotone"
                  dataKey="paid"
                  name="Encaissé"
                  stroke="#2e7d32"
                  strokeWidth={3}
                  dot={{
                    r: 3,
                  }}
                  activeDot={{
                    r: 5,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>


      <Divider
        sx={{
          my: 4,
        }}
      />

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title={`Ventes par ${SHOP.toLowerCase()}`}
          subtitle={`Comparaison des performances des ${SHOPS.toLowerCase()} ${RELAY_DEPOT}.`}
        />

        <DataTable
          rows={salesByStoreRows}
          columns={salesByStoreColumns}
          loading={loading}
          pageSize={5}
        />
      </Paper>

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title="Évolution journalière des ventes"
          subtitle="Ventes, encaissements et restes clients par journée."
        />

        <DataTable
          rows={salesByDayRows}
          columns={salesByDayColumns}
          loading={loading}
          pageSize={10}
        />
      </Paper>

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title="Top produits"
          subtitle="Produits les plus vendus et marge générée."
        />

        <DataTable
          rows={topProductRows}
          columns={topProductColumns}
          loading={loading}
          pageSize={10}
        />
      </Paper>

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title="Alertes de stock faible"
          subtitle="Produits dont la quantité disponible est inférieure ou égale à 10."
        />

        <DataTable
          rows={lowStockRows}
          columns={lowStockColumns}
          loading={loading}
          pageSize={10}
        />
      </Paper>

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title="Produits dormants"
          subtitle="Produits en stock sans vente enregistrée durant les 30 derniers jours."
        />

        <DataTable
          rows={dormantProductRows}
          columns={dormantProductColumns}
          loading={loading}
          pageSize={10}
        />
      </Paper>

      <Paper elevation={0} sx={DASHBOARD_FILTER_SURFACE}>
        <SectionTitle
          title="Commandes récentes"
          subtitle={`Dernières commandes de stock de ${RELAY_DEPOT}.`}
        />

        <DataTable
          rows={recentOrderRows}
          columns={recentOrderColumns}
          loading={loading}
          pageSize={10}
          height={430}
        />
      </Paper>

      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={closeSnackbar}
          sx={{
            width: "100%",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default DepotRelaisDashboardPage